import { Component, computed, effect, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { auditTime } from 'rxjs';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';
import { DateFormatPipe } from 'src/app/pipes/date-format.pipe';
import { SortByPipe } from 'src/app/pipes/sortBy.pipe';
import { CurrencyFormatPipe } from '../../pipes/currency-format.pipe';
import { ActivityTrackingService } from 'src/app/services/activity-tracking.service';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { DeviceType } from 'src/app/models/enums/breakpoint.enum';
import { SeriesCommitmentWidgetComponent } from './series-commitment-widget/series-commitment-widget.component';
import { CumulativeChartComponent } from 'src/app/shared/cumulative-chart/cumulative-chart.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AssetClassPipe } from 'src/app/pipes/asset-class.pipe';
import { InfoTextBoxComponent } from '../info-text-box/info-text-box.component';
import { TvpiIrrFormatService } from 'src/app/services/tvpi-irr-format.service';
import { SeriesDistributionsWidgetComponent } from './series-distributions-widget/series-distributions-widget.component';
import { SeriesCcdWidgetComponent } from './series-ccd-widget/series-ccd-widget.component';
import { PortfolioHierarchy } from 'src/app/models/classes/investor-dashboard.class';
import { AggregatedPortfolioSeries } from 'src/app/models/classes/aggregated-portfolio-series.class';

@Component({
    selector: 'app-portfolio-summary',
    imports: [
        CommonModule,
        InfoTextBoxComponent,
        CumulativeChartComponent,
        SeriesCcdWidgetComponent,
        SeriesCommitmentWidgetComponent,
        SeriesDistributionsWidgetComponent,
        SortByPipe,
        DateFormatPipe,
        CurrencyFormatPipe,
        RouterModule,
        MatTooltipModule,
        TourMatMenu,
    ],
    templateUrl: './portfolio-summary.component.html',
    styleUrl: './portfolio-summary.component.scss',
})
export class PortfolioSummaryComponent {
    private assetClassPipe = inject(AssetClassPipe);
    private tvpiIrrFormat = inject(TvpiIrrFormatService);
    private breakpointService = inject(BreakpointService);
    activityTracking = inject(ActivityTrackingService);

    readonly portfolioSummary = input.required<PortfolioHierarchy>();
    readonly investorId = input.required<number>();
    readonly selectedAsOfDate = input<string>('');
    readonly iconSet = input<Record<string, string> | undefined>(undefined);
    readonly showFirstSeries = input<boolean>(false);

    private readonly deviceType = toSignal(
        this.breakpointService.getDeviceType().pipe(auditTime(50)),
        { initialValue: DeviceType.Web }
    );
    readonly isMobileDevice = computed(() => this.deviceType() === DeviceType.Handset);
    currency = 'USD';
    portfolio!: PortfolioHierarchy;

    private readonly buildPortfolio = effect(() => {
        const portfolioSummary = this.portfolioSummary();
        if (this.iconSet() && portfolioSummary) {
            this.portfolio = this.addPortfolioSummaryFields(portfolioSummary);
        }
    });

    expandSeriesPortfolio(seriesData: AggregatedPortfolioSeries, fromClick?: boolean) {
        seriesData.isExpanded = true;
        if (fromClick) {
            this.activityTracking.expandOrCollapseToggled(
                'series visuals',
                seriesData,
                'investor dashboard',
                seriesData.isExpanded
            );
        }
    }

    collapseSeriesPortfolio(seriesData: AggregatedPortfolioSeries) {
        seriesData.isExpanded = false;
        this.activityTracking.expandOrCollapseToggled(
            'series visuals',
            seriesData,
            'investor dashboard',
            seriesData.isExpanded
        );
    }

    addPortfolioSummaryFields(portfolio: PortfolioHierarchy) {
        const iconSet = this.iconSet() as Record<string, string>;
        this.tvpiIrrFormat.deriveIrrAndTvpiIconAndTitle(
            portfolio.qoQIrrChange,
            portfolio.irr ?? 0,
            portfolio.priorIrr ?? 0,
            iconSet,
            portfolio
        );
        this.tvpiIrrFormat.deriveIrrAndTvpiIconAndTitle(
            portfolio.qoQTvpiChange,
            portfolio.tvpi ?? 0,
            portfolio.priorTvpi ?? 0,
            iconSet,
            portfolio,
            true
        );
        portfolio.formattedTvpi = this.tvpiIrrFormat.getTvpiValue(portfolio.tvpi);
        portfolio.formattedIrr = this.tvpiIrrFormat.getIrrValue(portfolio.irr);
        portfolio.priorPeriod = portfolio.reportingPeriod.slice(0, 10) != this.selectedAsOfDate();
        const sortedPortfolioSeries = portfolio.portfolioSeries.sort((a, b) => a.ticker.localeCompare(b.ticker));
        sortedPortfolioSeries.forEach((series) => {
            this.addPortfolioSeriesSummaryFields(portfolio, series, series === sortedPortfolioSeries[0]);
        });
        return portfolio;
    }

    addPortfolioSeriesSummaryFields(
        portfolio: PortfolioHierarchy,
        series: AggregatedPortfolioSeries,
        isFirstSeries: boolean
    ) {
        series.currency = portfolio.currency;
        series.isExpanded = false;
        const iconSet = this.iconSet() as Record<string, string>;
        this.tvpiIrrFormat.deriveIrrAndTvpiIconAndTitle(
            series.qoQIrrChange,
            series.irr ?? 0,
            series.priorIrr ?? 0,
            iconSet,
            series
        );
        this.tvpiIrrFormat.deriveIrrAndTvpiIconAndTitle(
            series.qoQTvpiChange,
            series.tvpi ?? 0,
            series.priorTvpi ?? 0,
            iconSet,
            series,
            true
        );
        series.assetClassImage = [];
        series.formattedTvpi = this.tvpiIrrFormat.getTvpiValue(series.tvpi);
        series.formattedIrr = this.tvpiIrrFormat.getIrrValue(series.irr);

        if(series.assetClass === 'ARS') {
          series.assetClass = 'Absolute Returns Strategy';
        }
        if(series.assetClass)
        {
          series.assetClassImage.push([series.assetClass, (this.iconSet() as Record<string, string>)[this.assetClassPipe.transform(series.assetClass) as string]]);
        }

        this.checkIfSeriesHasCommitments(series);
        if (isFirstSeries && this.showFirstSeries()) {
            series.isExpanded = true;
        }
        series.routerLink = this.redirectToSeriesPage(series);
        series.queryParams = { reportingdate: series.reportingPeriod.slice(0, 10) };
    }

    redirectToSeriesPage(seriesData: AggregatedPortfolioSeries) {
        const seriesId: number = seriesData.displayPortfolioSeriesMasterId ?? seriesData.portfolioMasterId;
        const route = `/investor/${this.investorId()}/series/${seriesId}`;
        return route;
    }

    checkIfSeriesHasCommitments(series: AggregatedPortfolioSeries) {
        if (series.portfolioSeriesBalanceSummary?.length > 0) {
            const currentPeriodSummary = series.portfolioSeriesBalanceSummary.filter(
                (s) => s.reportingPeriod == series.reportingPeriod
            );
            series.hasCommitments = currentPeriodSummary[0]?.hasInvestmentCommitments;
        }
    }
}
